import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Pause, Play, Trash2, Search } from "lucide-react";
import { useLogTail } from "../hooks";

const LEVELS = ["ALL", "DEBUG", "INFO", "WARNING", "ERROR"];

const LEVEL_COLORS = {
  DEBUG: "text-muted-soft",
  INFO: "text-[var(--text-primary)]",
  WARNING: "text-accent-amber",
  ERROR: "text-negative",
  CRITICAL: "text-negative",
};

export function LogsDrawer({ open, onOpenChange }) {
  const [level, setLevel] = useState("ALL");
  const [query, setQuery] = useState("");
  const [paused, setPaused] = useState(false);

  const { records, error, clear } = useLogTail({
    enabled: open,
    level,
    query,
    paused,
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        data-testid="logs-drawer"
        className="h-[70vh] bg-[var(--bg)] border-t border-soft p-0 rounded-none"
      >
        <SheetHeader className="px-6 py-4 border-b border-soft text-left space-y-0">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-muted-soft">
                System
              </p>
              <SheetTitle className="text-xl font-black tracking-tighter uppercase text-[var(--text-primary)] mt-1">
                Live Logs
                <span className="text-accent-amber ml-2">·</span>
              </SheetTitle>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-px bg-[var(--border-hex)] border border-soft">
                {LEVELS.map((lv) => (
                  <button
                    key={lv}
                    type="button"
                    onClick={() => setLevel(lv)}
                    data-testid={`log-level-${lv.toLowerCase()}`}
                    className={`font-mono-tight uppercase tracking-widest text-[10px] px-2.5 py-1.5 bg-surface transition-colors ${
                      level === lv
                        ? "text-accent-amber"
                        : "text-muted-soft hover:text-[var(--text-primary)]"
                    }`}
                  >
                    {lv}
                  </button>
                ))}
              </div>

              <div className="relative">
                <Search
                  size={12}
                  strokeWidth={1.5}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-soft pointer-events-none"
                />
                <Input
                  data-testid="log-search"
                  placeholder="GREP…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-52 pl-9 h-8 rounded-none border-soft bg-[var(--bg)] font-mono-tight uppercase tracking-widest text-[11px]"
                />
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPaused((p) => !p)}
                data-testid="logs-pause-toggle"
                className="rounded-none border-soft bg-surface font-mono-tight uppercase text-[10px] tracking-widest gap-1.5 h-8"
              >
                {paused ? (
                  <>
                    <Play size={12} strokeWidth={1.5} /> Resume
                  </>
                ) : (
                  <>
                    <Pause size={12} strokeWidth={1.5} /> Pause
                  </>
                )}
              </Button>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => clear()}
                data-testid="logs-clear"
                className="rounded-none border-soft bg-surface font-mono-tight uppercase text-[10px] tracking-widest gap-1.5 h-8"
              >
                <Trash2 size={12} strokeWidth={1.5} /> Clear
              </Button>
            </div>
          </div>

          <p className="text-[10px] uppercase tracking-[0.3em] text-muted-soft mt-3">
            <span className="num text-[var(--text-primary)]">
              {records.length}
            </span>{" "}
            records · polling{" "}
            <span className={paused ? "text-negative" : "text-positive"}>
              {paused ? "paused" : "every 2s"}
            </span>
          </p>
        </SheetHeader>

        <div
          data-testid="logs-scroll"
          className="overflow-auto px-6 py-4 font-mono-tight text-[12px] leading-relaxed h-[calc(70vh-130px)]"
        >
          {error ? (
            <div className="border border-[var(--negative)] bg-negative-soft p-4 text-negative">
              ▲ Failed to load logs: {error.message}
            </div>
          ) : records.length === 0 ? (
            <p className="text-muted-soft uppercase tracking-[0.3em] text-xs">
              · awaiting log events ·
            </p>
          ) : (
            <ul className="space-y-0.5">
              {records.map((r, i) => (
                <li
                  key={`${r.ts}-${i}`}
                  className="flex gap-3 items-baseline group"
                  data-testid={`log-row-${i}`}
                >
                  <span className="text-muted-soft shrink-0">
                    {r.ts.slice(11, 23)}
                  </span>
                  <span
                    className={`shrink-0 w-16 ${
                      LEVEL_COLORS[r.level] || "text-muted-soft"
                    }`}
                  >
                    {r.level}
                  </span>
                  <span className="text-muted-soft shrink-0 w-48 truncate">
                    {r.module}:{r.function}
                  </span>
                  <span className="text-[var(--text-primary)] break-all">
                    {r.message}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
